/**
 * KB-4351 — the three links a session takes from its program, at pre-fill and at "reset to program".
 *
 *   program.defaultSessionUrl → session.url         ("Session website")
 *   program.regUrl            → session.regUrl      ("Registration")
 *   program.programUrl        → session.programUrl  ("Program website", a copy of the program's)
 *
 * ⛔ The program page never becomes the session's `url`, and neither does the legacy `url` on the
 * program (the old "Information URL", which meant the program page AND the session default at once).
 * That double meaning is what put a program page in 2,179 offers' `url`. The legacy value stays
 * readable on the program until KB-4353 migrates it; it is not a default for anything.
 *
 * Every admin path that defaults a session's links goes through here: the session dialog, the
 * offer form's reset, bulk add, and the bulk-session job.
 */

import { programUrlProblem, type ProgramUrlProblem } from "./programUrlValidation";

export interface ProgramLinkSource {
  programUrl?: string | null;
  defaultSessionUrl?: string | null;
  regUrl?: string | null;
}

export interface SessionLinkDefaults {
  url: string;
  regUrl: string;
  programUrl: string;
}

export type SessionLinkField = keyof SessionLinkDefaults;

const text = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

export function sessionLinkDefaults(program: ProgramLinkSource | null | undefined): SessionLinkDefaults {
  return {
    url: text(program?.defaultSessionUrl),
    regUrl: text(program?.regUrl),
    programUrl: text(program?.programUrl),
  };
}

/** The program's value for one session link field, for a "reset to program" action. */
export function sessionLinkDefault(program: ProgramLinkSource | null | undefined, field: SessionLinkField): string {
  return sessionLinkDefaults(program)[field];
}

/**
 * The `programUrl` an offer-prep seam copies from its program. `value` is empty when the program has
 * none OR its stored one fails the program-page rules (`refused` then names why, for the log).
 */
export function resolveOfferProgramUrl(
  program: ProgramLinkSource | null | undefined,
): { value: string; candidate: string; refused: ProgramUrlProblem | null } {
  const candidate = sessionLinkDefaults(program).programUrl;
  const refused = programUrlProblem(candidate);
  return { value: refused ? "" : candidate, candidate, refused };
}

/**
 * KB-4346 — who wrote an offer's `programUrl`. `"program"`: copied from its program by an offer-prep
 * seam. `"cleared"`: the program's was cleared, so the copy was too. Either way the program owns the
 * field, and the nightly pipeline neither refills nor cards it
 * (`PROGRAM_OWNED_PROGRAM_URL_SOURCES` in `observedToOffersLib.cjs`). Absent: the pipeline's, or
 * written before KB-4346.
 */
export type ProgramUrlSource = "program" | "cleared";

export interface OfferProgramUrlFields {
  programUrl?: string | null;
  programUrlSource?: ProgramUrlSource;
}

/**
 * The `programUrl` fields an offer-prep seam writes, given the program and the stored offer (null on a
 * create). Payloads are applied with `update`, so an omitted key leaves the stored value alone.
 *
 *   - The program has an acceptable programUrl → copy it, stamped `"program"`.
 *   - The program has none, and the stored copy came from the program → clear it (`null`), stamped
 *     `"cleared"`. A programUrl the program never wrote (the pipeline's) is left alone.
 *   - The program's fails the rules → write nothing; `refused` names why, for the log.
 */
export function offerProgramUrlFields(
  program: ProgramLinkSource | null | undefined,
  storedOffer: Record<string, unknown> | null | undefined,
): { fields: OfferProgramUrlFields; candidate: string; refused: ProgramUrlProblem | null } {
  const { value, candidate, refused } = resolveOfferProgramUrl(program);
  if (value) return { fields: { programUrl: value, programUrlSource: "program" }, candidate, refused };
  if (!candidate && storedOffer?.programUrlSource === "program") {
    return { fields: { programUrl: null, programUrlSource: "cleared" }, candidate, refused };
  }
  return { fields: {}, candidate, refused };
}
